import { FormikProps } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { getIsUserSignedIn } from "@gamedev-web/gamedev-sdk-core";
import { OnboardingPageContainer } from "../../components";
import * as commonStyles from "../../components/commonStyles/commonStyles.module.css";
import { FormHeader } from "../../components/FormHeader";
import { SaveAndExitButton } from "../../components/SaveAndExitButton/SaveAndExitButton";
import { SplitPane } from "../../components/SplitPane";
import { SubFormNavigationProps } from "../../components/SubForm";
import { PageType } from "../../constants/pageType";
import { getPitchInformationFormUrl } from "../../helpers/navigationHelpers";
import { comparePitchApplicationStepStatus } from "../../helpers/pitchStatusHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { FormLocalizedTitleStringKey, Programs } from "../../models/programsConfig";
import { SubFormsRenderingModel } from "../../models/sharedFormsModels";
import { PitchApplicationStep } from "../../models/status";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { crmActions } from "../../redux/actions/crmApiActions";
import { getCurrentPitchConcept, getPitchApplicationStep } from "../../redux/selectors";
import { OnboardingPageAction } from "../../telemetry/models";
import { logPageAction } from "../../telemetry/pageAction";
import AboutYourGameSubForm from "./AboutYourGameSubForm/AboutYourGameSubForm";
import AboutYourselfSubForm from "./AboutYourself/AboutYourselfSubForm";
import AskForXboxSubForm from "./AskForXboxSubForm/AskForXboxSubForm";
import {
  PitchInformationFormId,
  PitchInformationSubFormsId,
  PitchInformationSubFormsIdsOrder,
  PitchInformationSubFormsLocalizedStringIds,
} from "./PitchInformationPageConstants";
import PublisherDeveloperSubForm from "./PublisherDeveloperSubForm/PublisherDeveloperSubForm";

interface RouteParams {
  pageId: string;
}

const PitchInformationPage = () => {
  const params = useParams<RouteParams>();
  const [currentSubFormIndex, setCurrentSubFormIndex] = useState(-1);
  const [
    pitchInformationTitle,
    pitchInformationPublisherDeveloperTitle,
    pitchAboutYourGameGameTitleTitle,
    pitchInformationAskForXboxTitle,
    pitchInformationAboutYourselfTitle,
  ] = useStrings(
    PitchInformationSubFormsLocalizedStringIds[FormLocalizedTitleStringKey],
    PitchInformationSubFormsLocalizedStringIds[
      PitchInformationSubFormsId.PublisherDeveloperInformation
    ],
    PitchInformationSubFormsLocalizedStringIds[PitchInformationSubFormsId.AboutYourGame],
    PitchInformationSubFormsLocalizedStringIds[PitchInformationSubFormsId.AskForXbox],
    PitchInformationSubFormsLocalizedStringIds[PitchInformationSubFormsId.AboutYourself]
  );
  const isSignedIn = useSelector(getIsUserSignedIn);
  const currentStep = useSelector(getPitchApplicationStep);
  const currentPitchConcept = useSelector(getCurrentPitchConcept);
  const history = useHistory();
  const dispatch = useDispatch();
  const formRef: React.RefObject<FormikProps<any>> = useRef<FormikProps<any>>(null);

  useEffect(() => {
    const navigateToPitchHome = () => history.replace(OnboardingUrls[RouteNames.PitchHome]);

    // TODO: For server-side rendering on this page, this redirection happens assuming the CRM API has previously been called and has updated the redux
    // state to the latest. Otherwise, the ApplicationStep will be Unset and it will always redirect to the Dashboard page.
    // Same thing would happen on PitchInformationPage.
    const isPitchIntroEnabled =
      isSignedIn &&
      currentStep &&
      comparePitchApplicationStepStatus(currentStep, PitchApplicationStep.PitchInformation) >= 0 &&
      comparePitchApplicationStepStatus(currentStep, PitchApplicationStep.ApplicationReviewed) < 0;

    if (!isPitchIntroEnabled) {
      navigateToPitchHome();
      return;
    }

    const subFormIndex = PitchInformationSubFormsIdsOrder.indexOf(
      params.pageId as PitchInformationSubFormsId
    );

    if (subFormIndex < 0) {
      history.push(OnboardingUrls[RouteNames.PitchHome]);
      return;
    }

    setCurrentSubFormIndex(subFormIndex < 0 ? 0 : subFormIndex);
  }, [params.pageId, history, isSignedIn, currentStep]);

  const subFormsList: ReadonlyDictionary<SubFormsRenderingModel> = {
    [PitchInformationSubFormsId.PublisherDeveloperInformation]: {
      title: pitchInformationPublisherDeveloperTitle,
      renderSubForm: (subFormProps: SubFormNavigationProps) => (
        <PublisherDeveloperSubForm {...subFormProps} />
      ),
    },
    [PitchInformationSubFormsId.AboutYourGame]: {
      title: pitchAboutYourGameGameTitleTitle,
      renderSubForm: (subFormProps: SubFormNavigationProps) => (
        <AboutYourGameSubForm {...subFormProps} />
      ),
    },
    [PitchInformationSubFormsId.AskForXbox]: {
      title: pitchInformationAskForXboxTitle,
      renderSubForm: (subFormProps: SubFormNavigationProps) => (
        <AskForXboxSubForm {...subFormProps} />
      ),
    },
    [PitchInformationSubFormsId.AboutYourself]: {
      title: pitchInformationAboutYourselfTitle,
      renderSubForm: (subFormProps: SubFormNavigationProps) => (
        <AboutYourselfSubForm {...subFormProps} />
      ),
    },
  };

  const onBackNavigation = () => {
    const previousSubFormIndex = currentSubFormIndex - 1;
    if (previousSubFormIndex >= 0) {
      logPageAction(OnboardingPageAction.BackFormButton);
      history.push(
        getPitchInformationFormUrl(PitchInformationSubFormsIdsOrder[previousSubFormIndex])
      );
    }
  };

  const onSaveFormToCrm = (subFormId: string) => {
    logPageAction(OnboardingPageAction.SaveAndNextFormButton);
    dispatch(
      crmActions.saveCrmData.start(
        Programs.Pitch,
        PitchInformationFormId,
        subFormId,
        currentPitchConcept?.id
      )
    );
  };

  const onForwardNavigation: () => void = () => {
    // Navigate to next sub-form or back to dashboard
    const nextSubFormIndex = currentSubFormIndex + 1;
    if (nextSubFormIndex < PitchInformationSubFormsIdsOrder.length) {
      history.push(getPitchInformationFormUrl(PitchInformationSubFormsIdsOrder[nextSubFormIndex]));
    } else {
      history.push(OnboardingUrls[RouteNames.PitchHome]);
    }
  };

  const onCancel = () => {
    logPageAction(OnboardingPageAction.CancelFormButton);
    history.push(OnboardingUrls[RouteNames.PitchHome]);
  };

  const renderSubForm = (subFormIndex: number) => {
    const subFormId = PitchInformationSubFormsIdsOrder[subFormIndex];
    const subFormProps: SubFormNavigationProps = {
      formRef,
      onCancel: subFormIndex === 0 ? onCancel : undefined,
      onBackNavigation: subFormIndex > 0 ? onBackNavigation : undefined,
      onSaveFormToCrm,
      onForwardNavigation,
    };

    return subFormsList[subFormId].renderSubForm(subFormProps);
  };

  const currentSubFormId = PitchInformationSubFormsIdsOrder[currentSubFormIndex];
  return (
    <OnboardingPageContainer pageType={PageType.PitchApplication}>
      <SplitPane rightPane={<div></div>}>
        <SaveAndExitButton
          formRef={formRef}
          formId={PitchInformationFormId}
          subFormId={currentSubFormId}
          onSaveToCrm={() => onSaveFormToCrm(currentSubFormId)}
          program={Programs.Pitch}
          programDashboardRouteName={RouteNames.PitchHome}
        />
        {currentSubFormIndex >= 0 && (
          <div className={commonStyles.formWrapper}>
            <FormHeader
              subFormsMetadata={subFormsList}
              currentSubFormId={PitchInformationSubFormsIdsOrder[currentSubFormIndex]}
              subFormsIdsOrder={PitchInformationSubFormsIdsOrder}
              mainFormTitle={pitchInformationTitle}
            />
            {renderSubForm(currentSubFormIndex)}
          </div>
        )}
      </SplitPane>
    </OnboardingPageContainer>
  );
};

export default PitchInformationPage;
